import React, { useEffect, useState } from "react";
import {
  defaultEmployeePointsDateRange,
  employeePointsApi,
} from "../api/employeePoints";
import { fillMissingDailyPoints } from "../modules/points/formatScores";
import type { EmployeeScore } from "../modules/points/types";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import EmployeeScoreCard from "./EmployeeScoreCard";

function EmployeeScoreCardSkeleton() {
  return (
    <Card className="py-4">
      <div className="flex items-center justify-between px-6">
        <div className="min-w-0 flex-1">
          <Skeleton className="h-5 w-32 mb-1" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-12 w-16 rounded-lg" />
      </div>
    </Card>
  );
}

interface EmployeeScoresListProps {
  preview?: boolean;
  onScoresLoaded?: (hasScores: boolean) => void;
}

const EmployeeScoresList: React.FC<EmployeeScoresListProps> = ({ preview = false, onScoresLoaded }) => {
  const [scores, setScores] = useState<EmployeeScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchScores = async () => {
      try {
        const dateRange = defaultEmployeePointsDateRange();
        const data = await employeePointsApi.getAll(dateRange);
        const displayData = preview
          ? data
          : fillMissingDailyPoints(data, dateRange);
        const sortedData = [...displayData].sort((a, b) => {
          if (a.latestPoints === null && b.latestPoints === null) return 0;
          if (a.latestPoints === null) return 1;
          if (b.latestPoints === null) return -1;
          return b.latestPoints - a.latestPoints;
        });
        setScores(sortedData);
        const scoredCount = sortedData.filter((s) => s.latestPoints !== null).length;
        onScoresLoaded?.(scoredCount > 0);
      } catch (_err) {
        setError("Something went wrong loading your employee scores.");
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [onScoresLoaded, preview]);

  if (loading) {
    return (
      <div className="grid gap-4">
        <EmployeeScoreCardSkeleton />
        <EmployeeScoreCardSkeleton />
        <EmployeeScoreCardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive px-4 py-3 rounded-md">
        <Text variant="sm" className="text-destructive">{error}</Text>
      </div>
    );
  }

  if (scores.length === 0) {
    return (
      <div className="text-center py-8">
        <Text variant="sm" className="text-muted-foreground">
          No employee scores available yet.
        </Text>
      </div>
    );
  }

  const globalMax = Math.max(
    0,
    ...scores.flatMap((s) =>
      s.recentPointsCalculations.map((c) => c.totalPoints ?? 0)
    )
  );

  const scoredEmployees = scores.filter((s) => s.latestPoints !== null);

  if (preview) {
    const topThree = scoredEmployees.slice(0, 3);
    const topIds = new Set(topThree.map((s) => s.id));
    const bottomThree = scoredEmployees.slice(-3).reverse().filter((s) => !topIds.has(s.id));

    const showSections = bottomThree.length > 0;

    return (
      <div className="space-y-6">
        <div>
          {showSections && (
            <Heading level={3} variant="xs" className="text-muted-foreground tracking-wider mb-3">
              Highest Scoring
            </Heading>
          )}
          <div className="grid gap-4">
            {topThree.map((score) => (
              <EmployeeScoreCard key={score.id} employeeScore={score} globalMax={globalMax} preview />
            ))}
          </div>
        </div>
        {showSections && (
          <>
            <Text as="span" className="block text-3xl text-muted-foreground/50 py-2">
              ⋯
            </Text>
            <div>
              <Heading level={3} variant="xs" className="text-muted-foreground tracking-wider mb-3">
                Lowest Scoring
              </Heading>
              <div className="grid gap-4">
                {bottomThree.map((score) => (
                  <EmployeeScoreCard key={score.id} employeeScore={score} globalMax={globalMax} preview />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {scoredEmployees.length === 0 && (
        <Card className="border-primary/30 bg-primary/5 px-4 py-3">
          <Text variant="sm" className="font-medium text-primary">
            Points have not been calculated yet for employees.
          </Text>
          <Text variant="sm" className="text-muted-foreground mt-1">
            Scores will appear here once the next calculation runs.
          </Text>
        </Card>
      )}
      <div className="grid gap-4">
        {scores.map((score) => (
          <EmployeeScoreCard key={score.id} employeeScore={score} globalMax={globalMax} />
        ))}
      </div>
    </div>
  );
};

export default EmployeeScoresList;

